import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// import { getUserInfo, UserInfo } from '../api/launchTrackerApi'
import { AppThunk } from "../app/store";
import { getUser } from "../app/api";
import { Requester } from "../../server/asin-central/model";

interface UserInfoState {
  userName: string;
  isLoading: boolean;
  /**
   * Lifecycle of the `/get-user` request. `isLoading` cannot express it: it
   * starts `true` before anything is dispatched, so it cannot tell "not
   * requested" from "in flight", nor "resolved" from "resolved empty".
   */
  status: UserInfoStatus;
  error: string | null;
  featureFlags: {
    loomAgentAssistantEnabled: boolean;
  };
}

export type UserInfoStatus = "idle" | "loading" | "succeeded" | "failed";

const userInfoInitialState: UserInfoState = {
  userName: "",
  isLoading: true,
  status: "idle",
  error: null,
  featureFlags: {
    loomAgentAssistantEnabled: false,
  },
};

const userInfo = createSlice({
  name: "userInfo",
  initialState: userInfoInitialState,
  reducers: {
    getUserInfoStarted(state) {
      state.isLoading = true;
      state.status = "loading";
    },
    getUserInfoSuccess(
      state,
      action: PayloadAction<{
        userName: string;
        featureFlags?: { loomAgentAssistantEnabled: boolean };
      }>
    ) {
      state.userName = action.payload.userName;
      state.featureFlags.loomAgentAssistantEnabled =
        action.payload.featureFlags?.loomAgentAssistantEnabled ?? false;
      state.isLoading = false;
      state.status = "succeeded";
      state.error = null;
    },
    getUserInfoFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.status = "failed";
      state.error = action.payload;
    },
  },
});

export const { getUserInfoStarted, getUserInfoSuccess, getUserInfoFailure } =
  userInfo.actions;

export default userInfo.reducer;

/**
 * Fetches the requester identity, at most once per page load. Dispatched from
 * several independent mount points (RUM init, the user badge, the
 * submission-history drawer), so without the dedupe it fires 2–3 times
 * concurrently. Pass `{ force: true }` to re-fetch deliberately.
 */
export const fetchUserInfo =
  (options: { force?: boolean } = {}): AppThunk =>
  async (dispatch, getState) => {
    const { status } = getState().userInfo;
    if (!options.force && (status === "loading" || status === "succeeded")) {
      return;
    }
    try {
      dispatch(getUserInfoStarted());
      const requester: Requester = await getUser();
      dispatch(
        getUserInfoSuccess({
          userName: requester.requester.identity,
          featureFlags: (requester as any).featureFlags,
        })
      );
    } catch (err: any) {
      dispatch(getUserInfoFailure(err.toString()));
    }
  };

type WithUserInfo = { userInfo: UserInfoState };

export const selectUserName = (state: WithUserInfo) => state.userInfo.userName;

export const selectUserInfoStatus = (state: WithUserInfo) =>
  state.userInfo.status;

/**
 * True only once `/get-user` has resolved with a non-empty identity. Requests
 * that carry the requester identity downstream must be gated on this.
 */
export const selectIsIdentityHydrated = (state: WithUserInfo) =>
  state.userInfo.status === "succeeded" &&
  isHydratedIdentity(state.userInfo.userName);

/** An identity is usable only when it is a non-blank string. */
export const isHydratedIdentity = (identity?: string | null): boolean =>
  typeof identity === "string" && identity.trim().length > 0;

export const selectIsLoomAgentAssistantEnabled = (state: WithUserInfo) =>
  state.userInfo.featureFlags.loomAgentAssistantEnabled;
